/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: types.ts (Appearance Types)
 🗓️ Created: 2025-01-XX
 🧩 Description:
    TypeScript types and Zod schemas for Appearance module.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { z } from 'zod';

/**
 * Widget Appearance Settings
 */
export interface WidgetPrefs {
  borderRadius: number;
  opacity: number;
  headerVisible: boolean;
  shadowIntensity: 'none' | 'light' | 'medium' | 'heavy';
  headerColor: string;
  bodyColor: string;
  textColor: string;
  borderColor: string;
  cardColor: string;
}

/**
 * Default Widget Settings (Dark Theme)
 */
export const DEFAULT_WIDGET_DARK: WidgetPrefs = {
  borderRadius: 12,
  opacity: 100,
  headerVisible: true,
  shadowIntensity: 'medium',
  headerColor: '#1e293b',
  bodyColor: '#0f172a',
  textColor: '#f1f5f9',
  borderColor: '#334155',
  cardColor: '#1e293b'
};

/**
 * Default Widget Settings (Light Theme)
 */
export const DEFAULT_WIDGET_LIGHT: WidgetPrefs = {
  borderRadius: 12,
  opacity: 100,
  headerVisible: true,
  shadowIntensity: 'medium',
  headerColor: '#ffffff',
  bodyColor: '#ffffff',
  textColor: '#1e293b',
  borderColor: '#e2e8f0',
  cardColor: '#ffffff'
};

/**
 * Widget Schema
 */
const widgetSchema = z.object({
  borderRadius: z.number().min(0).max(24),
  opacity: z.number().min(50).max(100),
  headerVisible: z.boolean(),
  shadowIntensity: z.enum(['none', 'light', 'medium', 'heavy']),
  headerColor: z.string(),
  bodyColor: z.string(),
  textColor: z.string(),
  borderColor: z.string(),
  cardColor: z.string()
});

/**
 * Appearance Form Schema
 */
export const appearanceSchema = z.object({
  mode: z.enum(['light', 'dark', 'system']),
  darkModeVariant: z.enum(['mint', 'navy', 'mirage', 'cinder', 'black', 'pure-black', 'dark-gray', 'very-dark-gray']),
  lightModeVariant: z.enum(['slate', 'gray', 'neutral', 'white', 'very-light-green', 'very-light-purple', 'light-green']),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  tooltips: z.object({
    lightTheme: z.object({
      backgroundColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      textColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      borderRadius: z.number().min(0).max(1)
    }),
    darkTheme: z.object({
      backgroundColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      textColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      borderRadius: z.number().min(0).max(1)
    })
  }),
  notifications: z.object({
    lightTheme: z.object({
      successColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      errorColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      warningColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      infoColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/)
    }).optional(),
    darkTheme: z.object({
      successColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      errorColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      warningColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      infoColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/)
    }).optional()
  }).optional(),
  // Dashboard Widget Settings
  widget: widgetSchema.optional(),
  widgetDark: widgetSchema.optional(),
  widgetLight: widgetSchema.optional(),
  gridRowHeight: z.number().min(1).max(120).optional(),
  gridColumns: z.number().min(6).max(48).optional(),
  dashboardColumns: z.union([z.literal(2), z.literal(3), z.literal(4)]).optional(),
  autoRefreshInterval: z.number().min(10).max(600).optional(),
  // Global UI border radius (in rem, 0 = sharp, 1 = very rounded)
  borderRadius: z.number().min(0).max(1).optional()
});

/**
 * Appearance Form Type
 */
export type AppearanceForm = z.infer<typeof appearanceSchema>;

/**
 * Color Preset
 */
export interface ColorPreset {
  name: string;
  primary: string;
  secondary: string;
}

/**
 * Theme Mode Option
 */
export interface ThemeModeOption {
  value: 'light' | 'dark' | 'system';
  label: string;
}

/**
 * Dark Mode Variant Option
 */
export interface DarkModeVariantOption {
  value: 'mint' | 'navy' | 'mirage' | 'cinder' | 'black' | 'pure-black' | 'dark-gray' | 'very-dark-gray';
  scheme: 'mint' | 'navy' | 'mirage' | 'cinder' | 'black' | 'pure-black' | 'dark-gray' | 'very-dark-gray';
}

/**
 * Light Mode Variant Option
 */
export interface LightModeVariantOption {
  value: 'slate' | 'gray' | 'neutral' | 'white' | 'very-light-green' | 'very-light-purple' | 'light-green';
  scheme: 'slate' | 'gray' | 'neutral' | 'white' | 'very-light-green' | 'very-light-purple' | 'light-green';
}

